import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import siteContent from '../../content/site/content.json';
import { Button } from '../ui/button';
import * as Icons from 'lucide-react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { WebIcon, GooglePlayIcon, AppleIcon, MicrosoftIcon, GalaxyIcon } from '../ui/StoreIcons';

// Store row (all "Coming soon" except Web per user direction)
const STORES = [
  { name: 'Web', status: 'Available', Icon: WebIcon },
  { name: 'Google Play', status: 'Coming soon', Icon: GooglePlayIcon },
  { name: 'App Store', status: 'Coming soon', Icon: AppleIcon },
  { name: 'Microsoft Store', status: 'Coming soon', Icon: MicrosoftIcon },
  { name: 'Galaxy Store', status: 'Coming soon', Icon: GalaxyIcon },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const data = siteContent.hero;
  const heroImages = (data.heroImages && data.heroImages.length > 0)
    ? data.heroImages
    : [data.heroImage || '/screenshots/dashboard.webp'];
  const [slide, setSlide] = React.useState(0);

  React.useEffect(() => {
    if (heroImages.length < 2) return;
    const timer = setInterval(() => setSlide((s) => (s + 1) % heroImages.length), 4500);
    return () => clearInterval(timer);
  }, [heroImages.length]);
  const primaryHref = data.primaryCtaHref || 'https://app.resiax.com';
  const isExternalPrimary = primaryHref.startsWith('http');

  const scrollTo = (hash) => {
    if (!hash || !hash.startsWith('#')) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Primary CTA behaviour:
  //  - If external URL: standard link (opens in new tab)
  //  - Else if data.primaryCtaInterest is set: fire the interest-capture event
  //    (pre-fills the survey form) and scroll to #contact
  //  - Else: plain smooth-scroll to the hash target
  const handlePrimaryClick = () => {
    if (isExternalPrimary) return; // <a> handles this
    if (primaryHref.startsWith('/')) {
      navigate(primaryHref);
    } else if (data.primaryCtaInterest) {
      window.dispatchEvent(
        new CustomEvent('resiax:set-interest', { detail: { interestType: data.primaryCtaInterest } })
      );
      scrollTo('#contact');
    } else {
      scrollTo(primaryHref);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4 text-[#B5541B]" /> : null;
  };

  return (
    <section id="hero" className="relative flex items-center bg-[#FFFAF8] overflow-hidden pt-16 lg:pt-20">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#B5541B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4722F]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-6">
              <Icons.Shield className="w-4 h-4 text-[#B5541B]" />
              <span className="text-sm font-semibold text-[#B5541B]">{data.badgeText}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-[1.05] mb-6 tracking-tight">
              {data.title}
            </h1>

            {data.tagline && (
              <p className="text-lg sm:text-xl text-[#B5541B] font-semibold mb-4">
                {data.tagline}
              </p>
            )}

            <p className="text-base sm:text-lg text-[#3B3535]/70 mb-4 leading-relaxed max-w-xl">
              {data.description}
            </p>

            {data.missionLine && (
              <p className="text-base sm:text-lg font-semibold text-[#1F1E1E] mb-4 max-w-xl" data-testid="hero-mission-line">
                {data.missionLine}
              </p>
            )}

            {data.registerIntro && (
              <p className="text-sm text-[#3B3535]/60 mb-7 leading-relaxed max-w-xl" data-testid="hero-register-intro">
                {data.registerIntro}
              </p>
            )}

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              {isExternalPrimary ? (
                <a href={primaryHref} target="_blank" rel="noopener noreferrer" data-testid="hero-primary-cta">
                  <Button className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
                    {data.primaryCta}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              ) : (
                <Button
                  onClick={handlePrimaryClick}
                  data-testid="hero-primary-cta"
                  className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  {data.primaryCta}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              )}
            </div>

            {/* Secondary: scrolls to the four-part journey */}
            {data.secondaryCta && (
              <button
                onClick={() => scrollTo(data.secondaryCtaHref || '#how-it-works')}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors group mb-4 mr-6"
              >
                {data.secondaryCta}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}

            {/* Provider route */}
            {data.providerCta && (
              <Link
                to="/providers"
                data-testid="hero-provider-cta"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5541B] bg-[#B5541B]/5 border border-[#B5541B]/30 hover:bg-[#B5541B]/10 hover:border-[#B5541B]/60 px-4 py-2 rounded-full transition-all group mb-4"
              >
                {data.providerCta}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            )}

            {/* Tertiary link — "Claim founder access". Scrolls to FounderStrip
                AND pre-fills the interest_type so a subsequent form fill lands as
                Founder Access in Notion without the extra click. */}
            {data.tertiaryCta && (
              <button
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent('resiax:set-interest', { detail: { interestType: 'founder_access' } })
                  );
                  scrollTo(data.tertiaryCtaHref || '#founder');
                }}
                data-testid="hero-tertiary-cta"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors group mb-10"
              >
                {data.tertiaryCta}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}

            {/* Lower-commitment option: launch updates */}
            {data.updatesCta && (
              <div className="mb-6">
                <button
                  onClick={() => navigate('/updates')}
                  data-testid="hero-updates-cta"
                  className="inline-flex items-center gap-1.5 text-sm text-[#3B3535]/60 hover:text-[#B5541B] transition-colors group underline underline-offset-4 decoration-[#3B3535]/30 hover:decoration-[#B5541B]"
                >
                  {data.updatesCta}
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            )}

            {/* Hero badges (quick-scroll pills) */}
            <div className="flex flex-wrap gap-2">
              {(data.badges || []).map((badge, i) => (
                <button
                  key={i}
                  onClick={() => scrollTo(badge.target)}
                  data-testid={`hero-badge-${i}`}
                  className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full shadow-sm border border-gray-100 hover:border-[#B5541B]/40 hover:shadow-md transition-all duration-200 cursor-pointer group"
                >
                  {getIcon(badge.icon)}
                  <span className="text-xs font-medium text-[#1F1E1E] group-hover:text-[#B5541B] transition-colors">{badge.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right: rotating app screenshots in phone frame */}
          <div className="relative">
            <div className="relative aspect-[9/16] max-w-sm mx-auto lg:max-w-md">
              {/* Phone frame */}
              <div className="absolute inset-0 bg-[#1F1E1E] rounded-[3rem] p-3 shadow-2xl">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                  {heroImages.map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt="ResiAX app screenshot"
                      className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-in-out"
                      style={{ opacity: i === slide ? 1 : 0 }}
                      data-testid={i === 0 ? 'hero-app-screenshot' : undefined}
                    />
                  ))}
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-semibold text-[#1F1E1E]">Live · Evidence logged</span>
              </div>
            </div>
          </div>
        </div>

        {/* Store availability row */}
        <div className="mt-14 lg:mt-20 pt-8 border-t border-gray-200/60">
          <p className="text-center text-xs uppercase tracking-widest text-[#3B3535]/50 font-semibold mb-4">
            {data.storeRowLabel || 'Launching for web, Google Play, Apple App Store, Microsoft Store and Galaxy Store.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {STORES.filter((store) => store.status === 'Available').map((store) => {
              const StoreIcon = store.Icon;
              const available = store.status === 'Available';
              return (
                <div
                  key={store.name}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${
                    available
                      ? 'bg-[#1F1E1E] border-[#1F1E1E] text-white'
                      : 'bg-white border-gray-200 text-[#3B3535]/70'
                  } text-xs`}
                >
                  <StoreIcon className="w-4 h-4" />
                  <span className="font-semibold">{store.name}</span>
                  {!available && <span className="text-[10px] uppercase tracking-wider text-[#B5541B] font-bold">{store.status}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
